import React, { useState } from 'react';

import InfoIcon from 'icons/info.svg';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import TextButton from 'components/application/buttons/TextButton';
import ConditionalWrapper from 'components/application/ConditionalWrapper';
import SidePanel from 'components/application/SidePanel';
import Tooltip from 'components/application/Tooltip';

import BookingConnectionsSidePanelForm from 'components/bookings/BookingConnectionsSidePanelForm';
import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';
import FormFooter from 'components/sidepanels/FormFooter';
import ShowFooter from 'components/sidepanels/ShowFooter';
import BookingSidePanelShow from 'components/training/BookingSidePanelShow';

import { resourceShape } from 'components/helpers/serialisableResources';

export function isBookingSubmitBlocked({
  domainELearningCourse,
  eLearningAllowance,
  remainingCredits,
}) {
  const blockers = [];

  if (domainELearningCourse && !domainELearningCourse.attributes.custom) {
    if (!eLearningAllowance) {
      blockers.push('allowanceNotLoaded');
    } else if (remainingCredits < 0) {
      blockers.push('overCreditUsage');
    }
  }

  return blockers.length !== 0;
}

export default function BookingSidePanel({
  currentBooking,
  domainBooker,
  domainBookingSource,
  domainCourse,
  domainELearningCourse,
  domainPersonnel,
  domainPersonnelFetchMeta,
  domainPersonnelMeta,
  eLearningAllowance,
  endOfDomainPersonnelListRef,
  isELearningAllowanceError,
  onBookingCreateSubmit,
  onBookingGroupMemberDelete,
  onBookingGroupMemberSelect,
  onBookingReminderClick,
  onBookingUpdateSubmit,
  onCancel,
  onDateChange,
  onEditBookingClick,
  onInputChange,
  onRecordTraining,
  onRemoveBooking,
  onSubmit,
  removeErrorStyling,
  requestError,
  showBookingGroupView,
  sidePanelContext,
  sidePanelIsOpen,
  submitDisabled,
}) {
  const currentActor = useCurrentActor();

  const [scrolledAmount, setScrolledAmount] = useState(0);

  const {
    hasBookingEditableAccess,
    hasBookingGroupEditableAccess,
    hasBookingRemindableAccess,
    hasTrainingEditableAccess,
  } = useTrainingRegisterResources();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'training.BookingSidePanel',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const isBooking = domainBookingSource?.type === 'booking';
  const isBookingGroup = domainBookingSource?.type === 'bookingGroup';

  const isBookingWithBookingGroup =
    isBooking && !!domainBookingSource.relationships?.bookingGroup?.data;

  const isCourseCreditBlockable =
    domainELearningCourse && !domainELearningCourse.attributes.custom;

  const isPastBookingSource = moment
    .parseZone(domainBookingSource?.attributes.date)
    .isBefore(moment().startOf('day'));

  const persistedMembersCount = (() => {
    switch (true) {
      case sidePanelContext === 'new':
        return 0;
      case isBooking:
        return 1;
      case isBookingGroup:
        return domainBookingSource.meta.personnelCount;
      default:
        null;
    }
  })();

  const remainingCredits = (() => {
    if (
      !isCourseCreditBlockable ||
      !eLearningAllowance ||
      persistedMembersCount === null
    ) {
      return null;
    }
    return (
      eLearningAllowance.data.attributes.currentCredits -
      (currentBooking.personnelIds.length - persistedMembersCount)
    );
  })();

  const header = (function renderHeader() {
    return (
      <div className='popup__title popup__title--tertiary-split'>
        {sidePanelContext !== 'show' && (
          <h1 className='tw-mb-2 tw-text-s tw-font-medium tw-tracking-tight'>
            {(function determineTitle() {
              switch (sidePanelContext) {
                case 'new':
                  return i18nT(
                    `new.header.${domainCourse?.relationships?.eLearningCourse?.data ? 'eLearning' : 'default'}`,
                  );
                case 'edit':
                  // assumed that enrolments cannot be edited
                  return i18nT('edit.header');
              }
            })()}
          </h1>
        )}
        <h2 className='truncated-text-container--wrapped truncated-text-container--five-lines tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
          {domainCourse?.attributes.name}
        </h2>
      </div>
    );
  })();

  const body = (function renderBody() {
    if (!domainCourse) return null;

    switch (true) {
      case sidePanelContext === 'new' || sidePanelContext === 'edit':
        return (
          <BookingConnectionsSidePanelForm
            currentBooking={currentBooking}
            domainBookingSource={domainBookingSource}
            domainCourse={domainCourse}
            domainPersonnelCollection={
              isBooking ?
                domainPersonnel && [{ ...domainPersonnel }]
              : domainPersonnel
            }
            eLearningAllowance={eLearningAllowance}
            isELearningAllowanceError={isELearningAllowanceError}
            isFetching={domainPersonnelFetchMeta?.isFetching}
            onBookingGroupMemberDelete={onBookingGroupMemberDelete}
            onBookingGroupMemberSelect={onBookingGroupMemberSelect}
            onDateChange={onDateChange}
            onInputChange={onInputChange}
            remainingCredits={remainingCredits}
            removeErrorStyling={removeErrorStyling}
            requestError={requestError}
            showBookingGroupView={showBookingGroupView}
          />
        );
      case sidePanelContext === 'show' && !!domainBookingSource: {
        const totalReadableBookingCount =
          isBookingGroup ? domainPersonnelMeta?.totalReadableBookingCount : 1;
        const totalBookingGroupPersonnelCount =
          domainPersonnelMeta?.totalUnscopedCount;
        const totalWriteableBookingCount =
          domainPersonnelMeta?.totalWriteableBookingCount;
        const shouldDisplayBanner =
          currentActor.user.attributes.accessType !== 'personnel' &&
          (isBookingGroup || isBookingWithBookingGroup) &&
          !!domainPersonnelMeta;

        return (
          <BookingSidePanelShow
            booker={domainBooker}
            bookingGroupInfoBanner={
              shouldDisplayBanner && (
                <BookingGroupInfoBanner
                  isBookingGroup={isBookingGroup}
                  isBookingWithBookingGroup={isBookingWithBookingGroup}
                  isPastBookingSource={isPastBookingSource}
                  totalBookingGroupPersonnelCount={
                    totalBookingGroupPersonnelCount
                  }
                  totalReadableBookingCount={totalReadableBookingCount}
                  totalWriteableBookingCount={totalWriteableBookingCount}
                />
              )
            }
            course={domainCourse}
            domainBookingSource={domainBookingSource}
            domainBookingSourcePersonnelCollection={
              showBookingGroupView ?
                isBooking ?
                  domainPersonnel && [{ ...domainPersonnel }]
                : domainPersonnel
              : null
            }
            endOfListRef={endOfDomainPersonnelListRef}
            isFetching={domainPersonnelFetchMeta?.isFetching}
            shouldShowPersonnel={showBookingGroupView}
            sidePanelIsOpen={sidePanelIsOpen}
            totalReadableBookingCount={totalReadableBookingCount}
          />
        );
      }
    }
  })();

  const footer = (function renderFooter() {
    switch (true) {
      case sidePanelContext === 'new':
        return (
          <FormFooter
            onCancel={onCancel}
            onSubmit={() => {
              showBookingGroupView ?
                onBookingCreateSubmit()
              : onSubmit(currentBooking.courseName);
            }}
            onSubmitTooltip={
              isELearningAllowanceError ?
                i18nTCommon('bookings.errors.creditInfoUnavailable')
              : null
            }
            requestError={requestError}
            submitButtonDisabled={
              submitDisabled ||
              isBookingSubmitBlocked({
                domainELearningCourse,
                eLearningAllowance,
                remainingCredits,
              })
            }
            text={
              domainELearningCourse ?
                i18nT('new.footer.text')
              : i18nTCommon('bookings.addBooking')
            }
          />
        );
      case sidePanelContext === 'show': {
        const totalBookingGroupPersonnelCount =
          domainPersonnelMeta?.totalUnscopedCount;
        const totalReadableBookingCount =
          domainPersonnelMeta?.totalReadableBookingCount;
        const totalWriteableBookingCount =
          domainPersonnelMeta?.totalWriteableBookingCount;

        const hasFullReadAccess =
          domainPersonnelFetchMeta?.isSuccess &&
          totalBookingGroupPersonnelCount === totalReadableBookingCount;
        const hasFullWriteAccess =
          domainPersonnelFetchMeta?.isSuccess &&
          totalBookingGroupPersonnelCount === totalWriteableBookingCount;
        const isNotifiable =
          domainPersonnelFetchMeta?.isSuccess &&
          domainPersonnelMeta.notifiableSetCount > 0;
        const isBookingWithoutBookingGroup =
          isBooking && !domainBookingSource?.relationships?.bookingGroup?.data;

        let sendReminderDisabledTooltip;
        if (domainBookingSource?.type === 'bookingGroup') {
          if (!isNotifiable) {
            sendReminderDisabledTooltip = i18nT(
              'show.footer.sendReminder.disabledTooltip.bookingGroup',
            );
          }
        } else if (domainBookingSource?.type === 'booking') {
          if (
            !domainPersonnel ||
            (!domainPersonnel.email && !domainPersonnel.attributes?.email)
          ) {
            sendReminderDisabledTooltip = i18nTCommon('personnel.noEmail');
          }
        }

        return (
          <>
            {(showBookingGroupView ?
              isBooking || hasFullReadAccess
            : hasBookingRemindableAccess) &&
              !isPastBookingSource && (
                <div className='tw-h-14.5 tw-border-0 tw-border-t-1 tw-border-solid tw-border-grey-100'>
                  <ConditionalWrapper
                    condition={!!sendReminderDisabledTooltip}
                    wrapper={(children) => (
                      <Tooltip
                        className='tooltip-dark--max-w-xxs'
                        placement='left'
                        tooltip={sendReminderDisabledTooltip}
                        trigger='hover'
                      >
                        {children}
                      </Tooltip>
                    )}
                  >
                    <TextButton
                      color='blue'
                      disabled={!!sendReminderDisabledTooltip}
                      onClick={() => {
                        onBookingReminderClick(domainBookingSource);
                      }}
                      size='full'
                    >
                      {isBookingWithoutBookingGroup || isBookingGroup ?
                        i18nTCommon('shared.sendReminder')
                      : i18nT('show.footer.sendReminder.button')}
                    </TextButton>
                  </ConditionalWrapper>
                </div>
              )}
            {!domainELearningCourse &&
              (showBookingGroupView ?
                domainPersonnelMeta?.isTrainingEligible
              : hasTrainingEditableAccess) && (
                <div className='tw-h-14.5 tw-border-0 tw-border-t-1 tw-border-solid tw-border-grey-100'>
                  <TextButton
                    color='blue'
                    onClick={onRecordTraining}
                    size='full'
                  >
                    {isBookingWithoutBookingGroup || isBookingGroup ?
                      i18nTCommon('training.recordTraining')
                    : i18nT('show.footer.recordTraining.button.toPersonnel')}
                  </TextButton>
                </div>
              )}
            {(showBookingGroupView ?
              isBooking ? domainPersonnelMeta?.isEditingEligible
              : hasBookingGroupEditableAccess && hasFullWriteAccess
            : isBookingWithoutBookingGroup && hasBookingEditableAccess) && (
              <ShowFooter
                deleteDisabled={
                  !!domainBookingSource?.relationships?.activeRegistration?.data
                    ?.id
                }
                deleteDisabledTooltip={i18nT(
                  'show.footer.delete.disabledTooltip',
                )}
                deleteText={i18nT(
                  `show.footer.delete.button.${domainELearningCourse ? 'eLearning' : 'default'}`,
                )}
                editText={i18nT(
                  `show.footer.edit.button.${domainELearningCourse ? 'eLearning' : 'default'}`,
                )}
                isEditButtonHidden={
                  !!(domainELearningCourse || isPastBookingSource)
                }
                onDeleteClick={() =>
                  onRemoveBooking({ bookingId: domainBookingSource?.id })
                }
                onEditClick={onEditBookingClick}
              />
            )}
            {!isBookingWithoutBookingGroup &&
              !showBookingGroupView &&
              hasBookingEditableAccess && (
                <div className='tw-h-14.5 tw-border-0 tw-border-t-1 tw-border-solid tw-border-grey-100'>
                  <TextButton
                    color='red'
                    onClick={() =>
                      onRemoveBooking({ bookingId: domainBookingSource?.id })
                    }
                    size='full'
                  >
                    {i18nT('show.footer.delete.button.forPersonnel')}
                  </TextButton>
                </div>
              )}
          </>
        );
      }
      case sidePanelContext === 'edit':
        return (
          <FormFooter
            onCancel={onCancel}
            onSubmit={onBookingUpdateSubmit}
            submitButtonDisabled={
              (showBookingGroupView && !domainPersonnelFetchMeta?.isSuccess) ||
              submitDisabled ||
              isBookingSubmitBlocked({
                domainELearningCourse,
                eLearningAllowance,
                remainingCredits,
              })
            }
            text={i18nTCommon('shared.saveChanges')}
          />
        );
      default:
        return null;
    }
  })();

  return (
    <SidePanel
      bodyContent={body}
      closeCallback={onCancel}
      color='tertiary'
      contentContext={sidePanelContext}
      displayClose={sidePanelContext === 'show'}
      footerContent={footer}
      headerContent={header}
      isOpen={sidePanelIsOpen}
      onBackdropClick={onCancel}
      scrolledAmount={scrolledAmount}
      setScrolledAmount={setScrolledAmount}
      submitDisabled={submitDisabled}
    />
  );
}

export function BookingGroupInfoBanner({
  isBookingGroup,
  isBookingWithBookingGroup,
  isPastBookingSource,
  totalBookingGroupPersonnelCount,
  totalReadableBookingCount,
  totalWriteableBookingCount,
}) {
  const otherPersonnelCount =
    isBookingGroup ?
      totalBookingGroupPersonnelCount - totalReadableBookingCount
    : totalBookingGroupPersonnelCount - 1;

  const cannotViewAllPersonnel =
    totalBookingGroupPersonnelCount !== totalReadableBookingCount;

  const completeWritableAccess =
    totalWriteableBookingCount === totalBookingGroupPersonnelCount;

  const bookingGroupInfoText = (function renderBookingGroupInfoText() {
    switch (true) {
      case isBookingWithBookingGroup:
        return (
          <>
            <Trans
              components={{ span: <span className='tw-font-medium' /> }}
              i18nKey='training.BookingSidePanel.BookingGroupInfoBanner.bookingInBookingGroup.personnelCount'
              ns='components'
              values={{ count: otherPersonnelCount }}
            />
            {completeWritableAccess && !isPastBookingSource && (
              <Trans
                components={{
                  a: (
                    /* eslint-disable jsx-a11y/anchor-has-content */
                    <a
                      className='app-link tw-align-baseline tw-text-blue-500 hover:tw-text-blue-300 active:tw-text-blue-300'
                      href='/personnel?tab=bookings'
                    />
                    /* eslint-enable jsx-a11y/anchor-has-content */
                  ),
                }}
                i18nKey='training.BookingSidePanel.BookingGroupInfoBanner.bookingInBookingGroup.changes'
                ns='components'
              />
            )}
          </>
        );
      case isBookingGroup &&
        cannotViewAllPersonnel &&
        totalWriteableBookingCount > 0 &&
        totalWriteableBookingCount !== totalReadableBookingCount:
        return (
          <Trans
            components={{ span: <span className='tw-font-medium' /> }}
            i18nKey='training.BookingSidePanel.BookingGroupInfoBanner.bookingGroup.cannotViewAllOne'
            ns='components'
            values={{ count: otherPersonnelCount }}
          />
        );
      case isBookingGroup &&
        cannotViewAllPersonnel &&
        (totalWriteableBookingCount === 0 ||
          (totalWriteableBookingCount > 0 &&
            totalWriteableBookingCount === totalReadableBookingCount)):
        return (
          <Trans
            components={{ span: <span className='tw-font-medium' /> }}
            i18nKey='training.BookingSidePanel.BookingGroupInfoBanner.bookingGroup.cannotViewAllTwo'
            ns='components'
            values={{ count: otherPersonnelCount }}
          />
        );
      default:
        return null;
    }
  })();

  if (!bookingGroupInfoText) return null;

  return (
    <div className='tw-bg-cyan-025'>
      <div className='tw-flex tw-p-3'>
        <div>
          <InfoIcon
            className='[&_path]:tw-fill-cyan-800'
            height={18}
            width={18}
          />
        </div>
        <div>
          <p className='tw-m-0 tw-ml-3 tw-font-inter tw-text-m tw-text-cyan-800'>
            {bookingGroupInfoText}
          </p>
        </div>
      </div>
    </div>
  );
}

BookingSidePanel.propTypes = {
  currentBooking: PropTypes.shape({
    id: PropTypes.string,
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    notes: PropTypes.string,
    personnelIds: PropTypes.array,
  }).isRequired,
  domainBooker: resourceShape('simpleUser'),
  domainPersonnel: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(resourceShape('personnel')),
  ]),
  domainELearningCourse: resourceShape('eLearningCourse'),
  domainBookingSource: resourceShape(['booking', 'bookingGroup']),
  domainCourse: resourceShape('course'),
  domainPersonnelFetchMeta: PropTypes.object,
  domainPersonnelMeta: PropTypes.object,
  eLearningAllowance: PropTypes.object,
  onBookingCreateSubmit: PropTypes.func,
  onBookingGroupMemberDelete: PropTypes.func,
  onBookingGroupMemberSelect: PropTypes.func,
  onBookingReminderClick: PropTypes.func.isRequired,
  onBookingUpdateSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired,
  onEditBookingClick: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onRecordTraining: PropTypes.func,
  onRemoveBooking: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  removeErrorStyling: PropTypes.func,
  requestError: PropTypes.object,
  showBookingGroupView: PropTypes.bool,
  sidePanelContext: PropTypes.string,
  sidePanelIsOpen: PropTypes.bool,
  submitDisabled: PropTypes.bool,
  endOfDomainPersonnelListRef: PropTypes.func,
  isELearningAllowanceError: PropTypes.bool,
};
BookingGroupInfoBanner.propTypes = {
  isBookingGroup: PropTypes.bool,
  isBookingWithBookingGroup: PropTypes.bool,
  totalBookingGroupPersonnelCount: PropTypes.number,
  totalReadableBookingCount: PropTypes.number,
  totalWriteableBookingCount: PropTypes.number,
  isPastBookingSource: PropTypes.bool,
};
